<!--
  Full-screen bot chooser shown by AppBotLoader when no bot is selected yet
  (e.g. multi-bot users landing without a `bid` query param).
-->
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useBotsStore } from '@/stores/bots';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Card } from '@/components/ui/card';
import ComboBox from '@/components/forms/ComboBox.vue';
import type { AcceptableValue } from 'reka-ui';
import logoUrl from '@/assets/moin-logo.svg';

const emit = defineEmits<{
  (e: 'onBotChange', uniqueBotId: string): void;
}>();

const botsStore = useBotsStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const bots = computed(() => botsStore.botsGetter);
const selectedBotId = ref<AcceptableValue>(botsStore.currentBotId || null);

function changeBot(uniqueBotId: AcceptableValue) {
  if (!uniqueBotId || typeof uniqueBotId !== 'string') return;
  const query = { ...route.query, bid: uniqueBotId };
  router.replace({ query }).catch(() => true);
  emit('onBotChange', uniqueBotId);
}

watch(selectedBotId, (botId) => changeBot(botId));
</script>

<template>
  <div class="app-bot-select bg-neutral-100">
    <Card class="m-4 p-6">
      <div class="flex justify-center">
        <div style="height: 40px" class="mb-8">
          <img :src="logoUrl" alt="moin.ai" />
        </div>
      </div>
      <div class="w-[300px]">
        <ComboBox
          v-model:selected-item="selectedBotId"
          :items="bots"
          item-label="name"
          item-value="uniqueBotId"
          :select-placeholder="t('common.chooseABot')"
          :search-placeholder="t('common.chooseABot')"
          variant="framed"
          content-class="z-[10001] w-[300px]"
          data-cy="app-bot-select"
        />
      </div>
    </Card>
  </div>
</template>

<style scoped>
.app-bot-select {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000; /* Above loading */
}
</style>
